import React, { useState } from "react"
import Link from "components/atoms/Link/Link"
import Status from "components/atoms/Status/Status"
import { InfoContent } from "components/shared/InfoComponents"
import PlatformLink from "components/shared/PlatformLink"
import TransactionBlock from "components/shared/TransactionBlock"
import { useCustomQuery } from "hooks/useCustomQuery"
import { Outlet, useNavigate } from "react-router-dom"
import { AdminOffersService } from "services/api-service"
import { AdminOfferType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Spinner from "ui/components/atoms/spinner"
import { LabelH2, P } from "ui/components/atoms/typography"
import Accordion from "ui/components/molecules/accordion"
import { translate } from "ui/data"
import { formatDate } from "utils/date"

import OfferDetailsApplications from "./OfferDetailsApplications"
import OfferDetailsCampaigns from "./OfferDetailsCampaigns"

const OfferDetails = ({ loading, data }: { loading?: boolean; data?: AdminOfferType }) => {
  const navigate = useNavigate()

  const [showApplications, setShowApplications] = useState(false)

  const { isLoading: isLoadingCount, data: count } = useCustomQuery<number>(
    { key: ["count_applications", data?.id] },
    () => AdminOffersService.countApplications(`${data?.id}`),
  )

  const boostOffer = async () => {
    try {
      await AdminOffersService.boost(`${data?.id}`)
      window.alert(`Ogłoszenie ${data?.name} zostało podbite na górę listy!`)
    } catch (e) {
      SentryService.error("[ERROR AdminOffersService.getOffer]:", e)
    }
  }

  const extendOffer = async () => {
    try {
      await AdminOffersService.extend(`${data?.id}`)
      window.alert(`Ogłoszenie ${data?.name} zostało przedłużone o 30 dni!`)
    } catch (e) {
      SentryService.error("[ERROR AdminOffersService.extend]:", e)
    }
  }

  const deactivatedOffer = async () => {
    if (window.confirm(`Czy na pewno chcesz dezaktywować ogłoszenie ${data?.name}?`)) {
      try {
        await AdminOffersService.deactivated(`${data?.id}`)
        window.alert(`Ogłoszenie ${data?.name} zostało dezaktywowane!`)
      } catch (e) {
        SentryService.error("[ERROR AdminOffersService.deactivated]:", e)
      }
    }
  }

  const deleteOffer = async () => {
    if (window.confirm(`Czy na pewno chcesz usunąć ogłoszenie ${data?.name}?`)) {
      try {
        await AdminOffersService.permanentlyDelete(`${data?.id}`)
        window.alert(`Ogłoszenie ${data?.name} zostało usunięte!`)
      } catch (e) {
        SentryService.error("[ERROR AdminOffersService.permanentlyDelete]:", e)
      }
    }
  }

  if (loading || !data)
    return (
      <Row $justifyContent="center" $alignItems="center" $marginTop="XL">
        <Spinner />
      </Row>
    )

  return (
    <>
      <Wrapper>
        <Box $marginBottom="M">
          <Status label={data.status} />
          <Box $marginTop="XXS">
            <PlatformLink status={data.status === "active"} to={`/offers/${data.id}`} />
          </Box>
        </Box>
        <Content>
          <Column>
            <InfoContent>
              <P label="Aktywne" $marginBottom="0">
                {data.isActive ? "TAK" : "NIE"}
              </P>
              <P label="Plan" $marginBottom="0">
                {data.plan}
              </P>
              <P label="Stanowisko" $marginBottom="0">
                {translate(data.category)}
              </P>
              <P label="Kategoria" $marginBottom="0">
                {translate(data.subcategory)}
              </P>
              <P label="Data utworzenia" $marginBottom="0">
                {formatDate(data.dtCreated)}
              </P>
              <P label="Data wygaśnięcia" $marginBottom="0">
                {data.expiredDate ? formatDate(data.expiredDate) : "-"}
              </P>
              <P label="Ilość aplikacji" $marginBottom="0">
                {count}
              </P>
              <P label="Język ogłoszenia" $marginBottom="0">
                {data.language.toUpperCase()}
              </P>
            </InfoContent>
            <Box>
              <Button $type="secondary" $width="100%" icon="boost" onClick={boostOffer}>
                Podbij ogłoszenie
              </Button>
              <Button
                $type="secondary"
                $marginTop="XS"
                $width="100%"
                icon="clock"
                onClick={extendOffer}
              >
                Przedłuż ogłoszenie (30 dni)
              </Button>
              <Link
                $type="secondary"
                $width="100%"
                icon="target"
                $marginTop="XS"
                to={`/panel/offers/${data.id}/analytics`}
              >
                Zobacz statystyki
              </Link>
              <Button
                $width="100%"
                icon="check-square"
                $marginTop="XS"
                onClick={() => setShowApplications(true)}
              >
                Zobacz kandydatów
              </Button>
              <Button
                $type="secondary"
                $marginTop="M"
                $width="100%"
                icon="trash"
                onClick={deactivatedOffer}
              >
                Dezaktywuj ogłoszenie
              </Button>
              <Button
                $type="secondary"
                $width="100%"
                icon="trash"
                $background="red"
                $color="white"
                $hoverBackground="red"
                $hoverColor="white"
                $marginTop="XS"
                onClick={deleteOffer}
              >
                Usuń ogłoszenie
              </Button>
            </Box>
            <Accordion title="Informacje o profilu">
              <LabelH2 $marginBottom="XS">{data.profile.name}</LabelH2>
              <Link icon="eye" $type="secondary" to={`/panel/profiles/${data.profile.id}`}>
                Wyświetl szczegóły
              </Link>
            </Accordion>
          </Column>
          <Column>
            <Accordion title="Informacje o transakcjach">
              <TransactionsWrapper>
                {data.transactions.map(transaction => (
                  <TransactionBlock key={transaction.id} id={data.id} transaction={transaction} />
                ))}
              </TransactionsWrapper>
            </Accordion>
            <OfferDetailsCampaigns offerId={data.id} />
          </Column>
        </Content>
      </Wrapper>
      <OfferDetailsApplications
        id={data?.id}
        show={showApplications}
        setShow={setShowApplications}
      />
      <Outlet />
    </>
  )
}

export default OfferDetails

const TransactionsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const Column = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.M};
`

const Content = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${({ theme }) => theme.space.M};
`

const Wrapper = styled(Box)`
  margin-top: ${({ theme }) => theme.space.M};
`
